// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * PermissionService - Hardcoded VP-rechtenregels.
 *
 * Regels zijn constants in deze service - NOOIT uit de database.
 * Ze kunnen NOOIT worden overschreven door een data-aanpassing.
 *
 * SAD-referentie:
 *   ALWAYS_VISIBLE:   ['moduleEmergencyCard']
 *   NEVER_FOR_VP:     ['moduleFinancial', 'moduleNotaryFile', 'moduleIntakeData']
 *   NEVER_FOR_ANYONE: ['moduleNotaryFile', 'moduleIntakeData']
 */

// ── Hardcoded regels (onwijzigbaar) ──────────────────────────────────────────

/** Altijd zichtbaar voor vertrouwenspersoon, ongeacht instellingen. */
export const ALWAYS_VISIBLE_FOR_VP = [
  'moduleEmergencyCard',
] as const

/**
 * Nooit zichtbaar voor vertrouwenspersoon - ook niet als gebruiker dit toestaat.
 * Financieel is bewust uitgesloten: rekeningnummers, saldi, belastinggegevens.
 */
export const NEVER_VISIBLE_FOR_VP = [
  'moduleFinancial',
  'moduleNotaryFile',
  'moduleIntakeData',
] as const

/**
 * Nooit zichtbaar voor wie dan ook via de app (ook niet voor eigenaar via nabestaandenmodus).
 * Persoonsgegevens en notarisdossier zijn only-owner.
 */
export const NEVER_VISIBLE_FOR_ANYONE_VIA_APP = [
  'moduleNotaryFile',
  'moduleIntakeData',
] as const

// ── Types ─────────────────────────────────────────────────────────────────────

export type ModuleKey =
  | 'moduleEmergencyCard'
  | 'moduleFuneralWishes'
  | 'modulePersonalFarewell'
  | 'moduleMedicalWishes'
  | 'moduleDocumentIndex'
  | 'moduleMessages'
  | 'moduleDigitalEstate'
  | 'moduleLifeStory'
  | 'moduleFinancial'
  | 'moduleNotaryFile'
  | 'moduleIntakeData'

export type AccessRole = 'OWNER' | 'TRUSTED_PERSON' | 'NEXT_OF_KIN'

/**
 * Resolves whether a given role may access a module.
 *
 * Fail-closed: returns false on any uncertainty.
 * Hardcoded rules override all stored permissions.
 *
 * @param module       - the module key to check
 * @param role         - the requesting role
 * @param storedPerm   - the stored toggle value (true = VISIBLE, false = HIDDEN)
 * @returns            - true if access is granted
 */
export function resolveAccess(
  module: ModuleKey,
  role: AccessRole,
  storedPerm?: boolean,
): boolean {
  // Owner always has access to everything
  if (role === 'OWNER') return true

  // NEVER_FOR_ANYONE_VIA_APP - hard block for all non-owner roles
  if ((NEVER_VISIBLE_FOR_ANYONE_VIA_APP as readonly string[]).includes(module)) {
    return false
  }

  // TRUSTED_PERSON specific rules
  if (role === 'TRUSTED_PERSON') {
    // Hard block - NEVER, even if user flipped the toggle
    if ((NEVER_VISIBLE_FOR_VP as readonly string[]).includes(module)) {
      return false
    }
    // Hard allow - ALWAYS, even if user forgot to enable it
    if ((ALWAYS_VISIBLE_FOR_VP as readonly string[]).includes(module)) {
      return true
    }
    // Respect stored permission for all other modules
    return storedPerm === true
  }

  // NEXT_OF_KIN - same as trusted person for standard modules
  if (role === 'NEXT_OF_KIN') {
    if ((NEVER_VISIBLE_FOR_ANYONE_VIA_APP as readonly string[]).includes(module)) {
      return false
    }
    return storedPerm === true
  }

  // Unknown role - fail-closed
  return false
}

/**
 * Returns a sanitized permissions object for a TrustedPerson.
 * Applies hardcoded overrides on top of user-configured values.
 * Use this before persisting or displaying permissions.
 */
export function sanitizeVPPermissions(raw: {
  moduleFuneralWishes:    boolean
  modulePersonalFarewell: boolean
  moduleMedicalWishes:    boolean
  moduleDocumentIndex:    boolean
  moduleMessages:         boolean
  moduleDigitalEstate:    boolean
  moduleLifeStory:        boolean
}): typeof raw {
  return {
    moduleFuneralWishes:    raw.moduleFuneralWishes,
    modulePersonalFarewell: raw.modulePersonalFarewell,
    moduleMedicalWishes:    raw.moduleMedicalWishes,
    moduleDocumentIndex:    raw.moduleDocumentIndex,
    moduleMessages:         raw.moduleMessages,
    moduleDigitalEstate:    raw.moduleDigitalEstate,
    // moduleLifeStory is allowed but defaults hidden
    moduleLifeStory:        raw.moduleLifeStory,
    // Note: moduleFinancial / moduleNotaryFile / moduleIntakeData are
    // intentionally not in this object - they are never configurable for VP.
  }
}

/**
 * Returns a display-safe list of modules a VP can see.
 * Used to show in TrustedPersonPage what a VP will have access to.
 */
export const VP_CONFIGURABLE_MODULES: Array<{ key: string; label: string; defaultOn: boolean }> = [
  { key: 'moduleFuneralWishes',    label: 'Uitvaartwensen',       defaultOn: true },
  { key: 'modulePersonalFarewell', label: 'Persoonlijk afscheid', defaultOn: false },
  { key: 'moduleMedicalWishes',    label: 'Medische wensen',      defaultOn: false },
  { key: 'moduleDocumentIndex',    label: 'Documentwijzer',       defaultOn: true },
  { key: 'moduleMessages',         label: 'Berichtenservice',     defaultOn: true },
  { key: 'moduleDigitalEstate',    label: 'Digitale nalatenschap',defaultOn: false },
  { key: 'moduleLifeStory',        label: 'Persoonlijk levensverhaal', defaultOn: false },
  // moduleEmergencyCard: ALWAYS zichtbaar - niet configureerbaar
  // moduleFinancial, moduleNotaryFile, moduleIntakeData: NOOIT zichtbaar - niet configureerbaar
]

/**
 * Modules that are ALWAYS visible to VP - shown as locked-on in UI.
 */
export const VP_ALWAYS_VISIBLE_MODULES: Array<{ key: string; label: string }> = [
  { key: 'moduleEmergencyCard', label: 'Noodinformatiekaart' },
]

/**
 * Modules that are NEVER visible to VP - shown as locked-off with explanation in UI.
 */
export const VP_NEVER_VISIBLE_MODULES: Array<{ key: string; label: string; reason: string }> = [
  { key: 'moduleFinancial',   label: 'Financieel overzicht',   reason: 'Financiële gegevens zijn altijd afgeschermd voor vertrouwenspersonen.' },
  { key: 'moduleNotaryFile',  label: 'Notarisdossier',         reason: 'Het notarisdossier is uitsluitend voor uzelf bedoeld.' },
  { key: 'moduleIntakeData',  label: 'Persoonlijke gegevens',  reason: 'Persoonsgegevens zijn altijd afgeschermd.' },
]
